import React, { useState } from 'react';
import { Card, Button, Badge } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';
import { generateSecureCode } from '../utils/codeGenerator';
import { getSizeCategoryLabel } from '../utils/trasterosSize';

const IN_USE_STATES = ['activo', 'libre', 'pendiente', 'bloqueado'];

const CodeBadge = ({ estado }) => {
  const props = {
    libre: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
    activo: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    pendiente: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    bloqueado: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
    expirado: { bg: 'bg-gray-200', text: 'text-gray-600', border: 'border-gray-300' },
    sustituido: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' },
  }[estado] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${props.bg} ${props.text} ${props.border}`}>
      {estado}
    </span>
  );
};

const Codigos = () => {
  const { data: codigos, loading: loadingC, refresh: refreshC, insert, update } = useDatabase('codigos');
  const { data: clientes, loading: loadingCl } = useDatabase('clientes');
  const { data: trasteros, loading: loadingT } = useDatabase('trasteros');

  const [activeTab, setActiveTab] = useState('actuales');
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(null);

  const loading = loadingC || loadingCl || loadingT;
  const clienteNombre = (id) => clientes.find(c => c.id === id)?.nombre_completo || '—';

  const handleRotarCodigo = async (trasteroId, currentCode) => {
    const choice = prompt('Introduce un código manual, o deja en blanco para generar uno aleatorio:');
    if (choice === null) return; // Cancelado

    let newCode = choice.trim();
    const activeCodesList = codigos.filter(c => IN_USE_STATES.includes(c.estado));

    if (newCode) {
      if (activeCodesList.some(c => c.codigo === newCode)) {
        alert('Ese código ya está en uso actualmente y no se puede reutilizar hasta darle de baja.');
        return;
      }
    } else {
      newCode = generateSecureCode(activeCodesList.map(c => c.codigo));
    }

    setProcessing(trasteroId);
    try {
      if (currentCode) {
        await update(currentCode.id, {
          estado: 'sustituido',
          fecha_sustitucion: new Date().toISOString(),
          sincronizacion_puerta: 'pendiente_baja'
        });
      }

      const isAllocated = currentCode && currentCode.estado === 'activo';
      await insert({
        codigo: newCode,
        trastero_id: trasteroId,
        cliente_id: isAllocated ? currentCode.cliente_id : null,
        reserva_id: isAllocated ? currentCode.reserva_id : null,
        estado: isAllocated ? 'activo' : 'libre',
        fecha_activacion: isAllocated ? new Date().toISOString() : null,
        sincronizacion_puerta: 'pendiente_alta',
        notas_internas: newCode ? 'Asignado manualmente desde panel general' : 'Rotado aleatoriamente desde panel general'
      });
    } catch (err) {
      alert('Error rotando código: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const markAsSynced = async (id) => {
    setProcessing(id);
    try {
      await update(id, { sincronizacion_puerta: 'sincronizado' });
    } catch (err) {
      alert('Error al sincronizar: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const currentCodesMap = new Map();
  if (!loading) {
    trasteros.forEach(t => {
      const current = codigos.find(c => c.trastero_id === t.id && !['expirado', 'sustituido'].includes(c.estado));
      if (current) currentCodesMap.set(t.id, current);
    });
  }

  const codigosPendientes = codigos.filter(c => ['pendiente_alta', 'pendiente_baja'].includes(c.sincronizacion_puerta));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
            Gestión de Accesos
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-brand-text">Control de códigos de puerta, rotaciones y sincronización.</p>
        </div>
        <Button variant="ghost" onClick={refreshC} title="Actualizar datos">
          <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('actuales')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'actuales' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Vista General
        </button>
        <button
          onClick={() => setActiveTab('puerta')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'puerta' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Acciones en Puerta
          {codigosPendientes.length > 0 && (
            <span className="bg-brand-alert text-white text-[10px] px-1.5 py-0.5 rounded-full">{codigosPendientes.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('historial')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'historial' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Historial Completo
        </button>
      </div>

      {activeTab === 'actuales' && (
        <Card>
          <div className="p-4 border-b border-gray-100 flex gap-4">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por trastero o cliente..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-primary"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Trastero</th>
                  <th className="px-6 py-3 font-medium">Tamaño</th>
                  <th className="px-6 py-3 font-medium">Código Activo</th>
                  <th className="px-6 py-3 font-medium">Cliente Actual</th>
                  <th className="px-6 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium">Sinc. Puerta</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trasteros
                  .filter(t => t.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || clienteNombre(currentCodesMap.get(t.id)?.cliente_id).toLowerCase().includes(searchTerm.toLowerCase()))
                  .sort((a, b) => a.codigo.localeCompare(b.codigo, 'es', { numeric: true }))
                  .map(t => {
                    const code = currentCodesMap.get(t.id);
                    return (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-bold text-brand-dark">{t.codigo}</td>
                        <td className="px-6 py-4 text-brand-text">{getSizeCategoryLabel(t.size)}</td>
                        <td className="px-6 py-4 font-mono font-bold tracking-wider text-brand-dark">
                          {code ? code.codigo : <span className="text-gray-300">------</span>}
                        </td>
                        <td className="px-6 py-4 text-brand-text truncate max-w-[150px]">
                          {code?.cliente_id ? clienteNombre(code.cliente_id) : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {code ? <CodeBadge estado={code.estado} /> : <Badge variant="inactivo">Sin código</Badge>}
                        </td>
                        <td className="px-6 py-4">
                          {code?.sincronizacion_puerta === 'pendiente_alta' && <span className="text-xs text-amber-600 font-medium flex items-center gap-1"><SafeIcon icon={FiIcons.FiClock} /> Pend. Alta</span>}
                          {code?.sincronizacion_puerta === 'pendiente_baja' && <span className="text-xs text-red-600 font-medium flex items-center gap-1"><SafeIcon icon={FiIcons.FiClock} /> Pend. Baja</span>}
                          {(!code || code?.sincronizacion_puerta === 'sincronizado') && <span className="text-xs text-green-600 flex items-center gap-1"><SafeIcon icon={FiIcons.FiCheck} /> OK</span>}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="outline"
                            className="!py-1.5 !px-3 text-xs flex items-center gap-1 ml-auto"
                            onClick={() => handleRotarCodigo(t.id, code)}
                            disabled={processing === t.id}
                          >
                            <SafeIcon icon={processing === t.id ? FiIcons.FiLoader : FiIcons.FiRefreshCw} className={processing === t.id ? 'animate-spin' : ''} />
                            Rotar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === 'puerta' && (
        <Card className="border-t-4 border-t-brand-alert">
          <div className="p-4 border-b border-gray-100 bg-red-50/30">
            <h3 className="font-bold text-brand-dark flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-brand-alert" />
              Acciones Pendientes en Teclado Físico
            </h3>
            <p className="text-sm text-brand-text mt-1">Estos cambios de código deben introducirse manualmente en la puerta del local.</p>
          </div>
          <div className="overflow-x-auto">
            {codigosPendientes.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-3xl" />
                </div>
                <p className="text-brand-text font-medium">Todo está sincronizado con la puerta.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-brand-text">
                  <tr>
                    <th className="px-6 py-3 font-medium">Acción a realizar</th>
                    <th className="px-6 py-3 font-medium">Código</th>
                    <th className="px-6 py-3 font-medium">Trastero</th>
                    <th className="px-6 py-3 font-medium">Estado Sistema</th>
                    <th className="px-6 py-3 font-medium text-right">Confirmación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {codigosPendientes.map(c => {
                    const isAlta = c.sincronizacion_puerta === 'pendiente_alta';
                    return (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${isAlta ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
                            {isAlta ? '+ AÑADIR A PUERTA' : '- BORRAR DE PUERTA'}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-mono font-bold tracking-widest text-lg">{c.codigo}</td>
                        <td className="px-6 py-4 font-medium">{trasteros.find(t => t.id === c.trastero_id)?.codigo || '—'}</td>
                        <td className="px-6 py-4"><CodeBadge estado={c.estado} /></td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="primary"
                            className="!py-1.5 !px-3 text-xs"
                            onClick={() => markAsSynced(c.id)}
                            disabled={processing === c.id}
                          >
                            {processing === c.id ? 'Marcando...' : 'Marcar como Hecho'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'historial' && (
        <Card>
          <div className="p-4 border-b border-gray-100 flex gap-4">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en el historial..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-primary"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Código</th>
                  <th className="px-6 py-3 font-medium">Trastero</th>
                  <th className="px-6 py-3 font-medium">Cliente</th>
                  <th className="px-6 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium">F. Creación</th>
                  <th className="px-6 py-3 font-medium">F. Sustitución</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codigos
                  .filter(c => c.codigo.includes(searchTerm) || clienteNombre(c.cliente_id).toLowerCase().includes(searchTerm.toLowerCase()))
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono font-bold tracking-wider text-brand-dark">{c.codigo}</td>
                      <td className="px-6 py-4">{trasteros.find(t => t.id === c.trastero_id)?.codigo || '—'}</td>
                      <td className="px-6 py-4">{clienteNombre(c.cliente_id)}</td>
                      <td className="px-6 py-4"><CodeBadge estado={c.estado} /></td>
                      <td className="px-6 py-4 text-xs text-brand-text">{new Date(c.created_at).toLocaleDateString('es-ES')}</td>
                      <td className="px-6 py-4 text-xs text-brand-text">
                        {c.fecha_sustitucion ? new Date(c.fecha_sustitucion).toLocaleDateString('es-ES') : (c.fecha_expiracion ? new Date(c.fecha_expiracion).toLocaleDateString('es-ES') : '—')}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Codigos;